import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { makeStyles } from '@material-ui/styles';
import { CompanyLogosCarousel, JobTable } from './components';
import JobListSkeleton from './components/JobListSkeleton';
import EmptyJobState from './components/EmptyJobState';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
  Typography
} from '@material-ui/core';
import { APP_PATH_SIGNUP, CATEGORIES_JOB, TYPES_JOB } from '../../config';
import { useDispatch, useSelector } from 'react-redux';
import usePageTitle from '../../hooks/usePageTitle';
import useStructuredData from '../../hooks/useStructuredData';
import useMetaTags from '../../hooks/useMetaTags';
import useCanonicalUrl from '../../hooks/useCanonicalUrl';
import { generateWebSiteSchema } from '../../helpers/structuredDataHelpers';
import {
  clearFilters,
  enrichedJobListSelector,
  enrichedJobLoadingSelector,
  enrichedJobPaginationSelector,
  fetchEnrichedJobs,
  fetchStickyJobs,
  filterCategoriesUpdated,
  filterTypesUpdated,
  jobFilterCategoriesSelector,
  jobFilterTypesSelector,
  stickyJobListSelector,
  stickyJobsFetchedSelector,
  stickyJobsLoadingSelector
} from '../../redux/features/job/jobsSlice';
import { useCurrentUserInfo } from '../../hooks/useCurrentUserInfo';
import { useHistory } from 'react-router-dom';

const useStyles = makeStyles((theme) => ({
  jobListContainer: {
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'flex-start',
    width: '100%',
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      gap: theme.spacing(3)
    }
  },
  jobListContent: {
    flex: '1 1 auto',
    minWidth: 0,
    width: 0,
    [theme.breakpoints.down('md')]: {
      flex: '1 1 100%',
      width: '100%'
    }
  },
  filterSidebar: {
    flex: '0 0 240px',
    width: '240px',
    maxWidth: '240px',
    position: 'sticky',
    top: theme.spacing(10),
    alignSelf: 'flex-start',
    marginLeft: theme.spacing(4),
    [theme.breakpoints.down('md')]: {
      position: 'static',
      flex: '1 1 100%',
      width: '100%',
      maxWidth: '100%',
      order: -1,
      marginLeft: 0
    }
  },
  loadMoreContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
    gap: theme.spacing(2)
  },
  loadMoreButton: {
    textTransform: 'none',
    fontWeight: 600,
    padding: theme.spacing(1, 3),
    borderRadius: theme.spacing(2),
    fontSize: '0.9rem'
  },
  maxLoadsMessage: {
    padding: theme.spacing(3),
    textAlign: 'center',
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.background.paper,
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`
  }
}));

const FilterJobs = () => {
  const dispatch = useDispatch();
  const selectedCategory = useSelector(jobFilterCategoriesSelector);
  const selectedType = useSelector(jobFilterTypesSelector);

  const useStylesFilterJobs = makeStyles((theme) => ({
    filterSection: {
      padding: theme.spacing(3),
      borderRadius: 12,
      backgroundColor: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`
    },
    filterTitle: {
      marginBottom: theme.spacing(2),
      fontWeight: 600,
      color: theme.palette.text.primary
    },
    filterComponent: {
      marginBottom: theme.spacing(2),
      width: '100%'
    },
    filterActions: {
      display: 'flex',
      justifyContent: 'flex-end'
    },
    clearButton: {
      textTransform: 'none'
    },
    formControl: {
      width: '100%'
    }
  }));
  const classes = useStylesFilterJobs();

  const handleChangeCategory = (event) => {
    const value = event.target.value;
    dispatch(filterCategoriesUpdated({ categories: value }));
  };

  const handleChangeType = (event) => {
    const value = event.target.value;
    dispatch(filterTypesUpdated({ types: value }));
  };

  return (
    <div className={classes.filterSection}>
      <Typography variant="h6" className={classes.filterTitle}>
        Refine your search
      </Typography>
      <div className={classes.filterComponent}>
        <FormControl className={classes.formControl}>
          <Select
            id="category-filter"
            value={selectedCategory || ''}
            onChange={handleChangeCategory}
            displayEmpty>
            <MenuItem value="">
              <em>All Categories</em>
            </MenuItem>
            {CATEGORIES_JOB.map((category) => (
              <MenuItem key={category.value} value={category.label}>
                {category.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>
      <div className={classes.filterComponent}>
        <FormControl className={classes.formControl}>
          <Select
            id="type-filter"
            value={selectedType || ''}
            onChange={handleChangeType}
            displayEmpty>
            <MenuItem value="">
              <em>All Types</em>
            </MenuItem>
            {TYPES_JOB.map((type) => (
              <MenuItem key={type.value} value={type.label}>
                {type.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>
      {(selectedCategory || selectedType) && (
        <div className={classes.filterActions}>
          <Button
            size="small"
            className={classes.clearButton}
            onClick={() => dispatch(clearFilters())}>
            Clear filters
          </Button>
        </div>
      )}
    </div>
  );
};

const JobList = ({ showFilters, showCarousel = true }) => {
  const dispatch = useDispatch();
  const selectedCategory = useSelector(jobFilterCategoriesSelector);
  const selectedType = useSelector(jobFilterTypesSelector);
  const enrichedJobs = useSelector(enrichedJobListSelector);
  const isLoading = useSelector(enrichedJobLoadingSelector);
  const pagination = useSelector(enrichedJobPaginationSelector);
  const stickyJobs = useSelector(stickyJobListSelector);
  const stickyJobsLoading = useSelector(stickyJobsLoadingSelector);
  const stickyJobsFetched = useSelector(stickyJobsFetchedSelector);
  const classes = useStyles();
  const { isLogin } = useCurrentUserInfo();
  // Ref for intersection observer
  const sentinelRef = useRef(null);
  const observerRef = useRef(null);

  // Convert labels back to values for API filtering
  const categoryValue = useMemo(() => {
    if (!selectedCategory) return null;
    const category = CATEGORIES_JOB.find(
      (cat) => cat.label === selectedCategory
    );
    return category ? category.value : null;
  }, [selectedCategory]);

  const typeValue = useMemo(() => {
    if (!selectedType) return null;
    const type = TYPES_JOB.find((t) => t.label === selectedType);
    return type ? type.value : null;
  }, [selectedType]);

  // Set dynamic page title based on filters
  const pageTitle = useMemo(() => {
    if (selectedCategory && selectedType) {
      return `${selectedCategory} ${selectedType} Remote Jobs`;
    }
    if (selectedCategory) {
      return `${selectedCategory} Remote Jobs`;
    }
    if (selectedType) {
      return `${selectedType} Remote Jobs`;
    }
    return 'Your Remote Job Platform';
  }, [selectedCategory, selectedType]);

  usePageTitle(pageTitle);

  // Generate WebSite structured data for SEO (homepage)
  const websiteSchema = useMemo(() => {
    // Get the base URL from window.location
    const siteUrl = window.location.origin;
    return generateWebSiteSchema(siteUrl);
  }, []);

  // Inject structured data into page
  useStructuredData(websiteSchema, 'website-schema');

  // Fetch sticky jobs once on mount - only if not already fetched or loading
  useEffect(() => {
    if (!stickyJobsFetched) {
      console.log(
        '🔄 [JobList] Initiating sticky jobs fetch | fetched:',
        stickyJobsFetched,
        'loading:',
        stickyJobsLoading
      );
      dispatch(fetchStickyJobs());
    }
  }, [dispatch, stickyJobsFetched, stickyJobsLoading]);

  // Fetch enriched jobs when component mounts or filters change
  useEffect(() => {
    console.log(
      '🔄 [JobList] useEffect triggered - dispatching fetchEnrichedJobs'
    );
    console.log(
      '🔍 [JobList] Filters - Category:',
      categoryValue,
      'Type:',
      typeValue
    );
    dispatch(
      fetchEnrichedJobs({
        category: categoryValue,
        type: typeValue,
        page: 0,
        size: 10
      })
    );
  }, [categoryValue, typeValue, dispatch]);

  // Combine sticky jobs + regular jobs, removing duplicates (keep sticky version)
  const allJobs = useMemo(() => {
    console.log(
      '🔄 [JobList] Combining jobs - sticky:',
      stickyJobs.length,
      'regular:',
      enrichedJobs.length
    );

    // Log first few sticky jobs for debugging
    if (stickyJobs.length > 0) {
      console.log(
        '🔍 [JobList] First 3 sticky jobs:',
        stickyJobs.slice(0, 3).map((jr) => ({
          id: jr.job?.id,
          title: jr.job?.title,
          hasPlan: !!jr.plan,
          planType: jr.plan?.metadata?.id
        }))
      );
    }

    // Create a Set of sticky job IDs for fast lookup
    const stickyJobIds = new Set(
      stickyJobs.map((jobResult) => jobResult.job?.id).filter(Boolean)
    );

    console.log('🔍 [JobList] Sticky job IDs:', Array.from(stickyJobIds));

    // Filter out regular jobs that are already in sticky jobs
    const uniqueEnrichedJobs = enrichedJobs.filter((jobResult) => {
      const isDuplicate = stickyJobIds.has(jobResult.job?.id);
      if (isDuplicate) {
        console.log(
          '⚠️ [JobList] Filtering out duplicate job:',
          jobResult.job?.id,
          jobResult.job?.title
        );
      }
      return !isDuplicate;
    });

    console.log(
      '🔄 [JobList] After deduplication - sticky:',
      stickyJobs.length,
      'unique regular:',
      uniqueEnrichedJobs.length,
      'duplicates removed:',
      enrichedJobs.length - uniqueEnrichedJobs.length
    );

    // Log first few regular jobs for debugging
    if (uniqueEnrichedJobs.length > 0) {
      console.log(
        '🔍 [JobList] First 3 regular jobs:',
        uniqueEnrichedJobs.slice(0, 3).map((jr) => ({
          id: jr.job?.id,
          title: jr.job?.title,
          hasPlan: !!jr.plan,
          planType: jr.plan?.metadata?.id
        }))
      );
    }

    const combined = [...stickyJobs, ...uniqueEnrichedJobs];

    // Verify combined list has sticky jobs first
    console.log('✅ [JobList] Combined list - total:', combined.length);
    if (combined.length > 0) {
      console.log(
        '🔍 [JobList] First 5 combined jobs:',
        combined.slice(0, 5).map((jr, idx) => ({
          index: idx,
          id: jr.job?.id,
          title: jr.job?.title,
          isSticky: stickyJobIds.has(jr.job?.id),
          hasPlan: !!jr.plan,
          planType: jr.plan?.metadata?.id
        }))
      );
    }

    return combined;
  }, [stickyJobs, enrichedJobs]);

  // Generate Open Graph and Twitter Card meta tags
  const metaTags = useMemo(() => {
    // Create dynamic description based on job count and filters
    let description =
      'Find remote work or hire top talent — your platform for remote opportunities and hiring.';

    if (allJobs.length > 0) {
      description = `Browse ${allJobs.length} remote job opportunities`;

      if (selectedCategory && selectedType) {
        description += ` in ${selectedCategory} (${selectedType})`;
      } else if (selectedCategory) {
        description += ` in ${selectedCategory}`;
      } else if (selectedType) {
        description += ` (${selectedType})`;
      }

      description += '. Browse opportunities or post your open positions.';
    } else if (selectedCategory || selectedType) {
      description = 'Explore remote opportunities or find your next hire. ';
      if (selectedCategory && selectedType) {
        description += `Explore ${selectedCategory} ${selectedType} positions.`;
      } else if (selectedCategory) {
        description += `Explore ${selectedCategory} positions.`;
      } else if (selectedType) {
        description += `Explore ${selectedType} positions.`;
      }
    }

    return {
      title: pageTitle,
      description:
        description.length > 160
          ? description.substring(0, 157) + '...'
          : description,
      image: 'https://remote-job.ai/images/social-preview.jpg',
      url: 'https://remote-job.ai/jobs',
      type: 'website'
    };
  }, [allJobs.length, selectedCategory, selectedType, pageTitle]);

  // Apply meta tags
  useMetaTags(metaTags);

  // Set canonical URL
  useCanonicalUrl();

  // Load more function
  const loadMore = useCallback(() => {
    if (
      pagination.isLoadingMore ||
      !pagination.hasMore ||
      pagination.maxLoadsReached
    ) {
      return;
    }

    console.log(
      '📥 [JobList] Loading more jobs - page:',
      pagination.currentPage + 1
    );
    dispatch(
      fetchEnrichedJobs({
        category: categoryValue,
        type: typeValue,
        page: pagination.currentPage + 1,
        size: 10,
        isLoadMore: true
      })
    );
  }, [dispatch, categoryValue, typeValue, pagination]);

  // Setup intersection observer for infinite scroll
  useEffect(() => {
    if (!sentinelRef.current) return;

    // Cleanup previous observer
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    // Create new observer
    observerRef.current = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (
          entry.isIntersecting &&
          !pagination.isLoadingMore &&
          pagination.hasMore &&
          !pagination.maxLoadsReached
        ) {
          console.log('👁️ [JobList] Sentinel visible - triggering load more');
          if (isLogin) {
            loadMore();
          }
        }
      },
      {
        root: null,
        rootMargin: '200px', // Trigger 200px before reaching the sentinel
        threshold: 0.1
      }
    );

    observerRef.current.observe(sentinelRef.current);

    // Cleanup on unmount
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [
    isLogin,
    loadMore,
    pagination.isLoadingMore,
    pagination.hasMore,
    pagination.maxLoadsReached
  ]);

  console.log(
    '🎨 [JobList] Rendering with jobs - sticky:',
    stickyJobs.length,
    'regular:',
    enrichedJobs.length,
    'total:',
    allJobs.length
  );
  console.log('🎨 [JobList] Loading state:', isLoading);
  console.log('🎨 [JobList] Pagination:', pagination);

  // Check if any filters are active
  const hasActiveFilters = Boolean(selectedCategory || selectedType);

  // Handle reset filters
  const handleResetFilters = () => {
    dispatch(clearFilters());
  };

  // Check if initial loading (both sticky and regular jobs)
  const isInitialLoading =
    (stickyJobsLoading || isLoading) && allJobs.length === 0;
  const history = useHistory();
  const onLoadMoreBtn = () => {
    if (isLogin) {
      // Just load more jobs
      loadMore();
    } else {
      // Redirect to create an account /sign-up
      history.push(APP_PATH_SIGNUP);
    }
  };
  return (
    <section id="jobs" aria-label="Job listings">
      {/* Company logos carousel - spans full width above filters */}
      {showCarousel && !isInitialLoading && allJobs.length > 0 && (
        <CompanyLogosCarousel jobResults={allJobs} />
      )}

      <div className={classes.jobListContainer}>
        <div className={classes.jobListContent}>
          {isInitialLoading ? (
            <JobListSkeleton count={6} />
          ) : allJobs.length === 0 ? (
            <EmptyJobState
              hasFilters={hasActiveFilters}
              onResetFilters={handleResetFilters}
            />
          ) : (
            <>
              <JobTable jobResults={allJobs} isPreview={false} />

              {/* Load more section */}
              <div className={classes.loadMoreContainer}>
                {/* Intersection observer sentinel */}
                <div
                  ref={sentinelRef}
                  style={{ height: '1px', width: '100%' }}
                />

                {pagination.isLoadingMore && (
                  <Box display="flex" alignItems="center" gap={2}>
                    <CircularProgress size={24} />
                    <Typography variant="body2" color="textSecondary">
                      Loading more jobs...
                    </Typography>
                  </Box>
                )}

                {pagination.hasMore &&
                  !pagination.maxLoadsReached &&
                  !pagination.isLoadingMore &&
                  isLogin && (
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      className={classes.loadMoreButton}
                      onClick={onLoadMoreBtn}>
                      Load More Jobs
                    </Button>
                  )}
                {!isLogin && (
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    className={classes.loadMoreButton}
                    onClick={() => {
                      history.push(APP_PATH_SIGNUP);
                    }}>
                    Show More Jobs
                  </Button>
                )}
                {pagination.maxLoadsReached && (
                  <div className={classes.maxLoadsMessage}>
                    <Typography variant="body1" gutterBottom>
                      Maximum results loaded (450 jobs)
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Try using filters to narrow down your search
                    </Typography>
                  </div>
                )}

                {!pagination.hasMore &&
                  !pagination.maxLoadsReached &&
                  enrichedJobs.length > 0 &&
                  isLogin && (
                    <Typography variant="body2" color="textSecondary">
                      No more jobs to load
                    </Typography>
                  )}

                {allJobs.length > 10 && (
                  <Typography variant="caption" color="textSecondary">
                    Showing {allJobs.length} jobs ({stickyJobs.length} sticky +{' '}
                    {enrichedJobs.length} regular)
                  </Typography>
                )}
              </div>
            </>
          )}
        </div>
        {showFilters && (
          <div className={classes.filterSidebar}>
            <FilterJobs />
          </div>
        )}
      </div>
    </section>
  );
};

JobList.defaultProps = {
  showFilters: false,
  showCarousel: true
};

JobList.propTypes = {
  showFilters: PropTypes.bool,
  showCarousel: PropTypes.bool
};

export default JobList;
